import {
  ACCESSIBILITY_ARGS,
  ALL_LAYOUT_ARGS,
  Metadata,
  pick,
} from '../../../../../utils/spds/metadata';
import { concat, hash } from '@ember/helper';
import { or } from 'ember-truth-helpers';
import FlexRow from '../../../layout/flex/row.gjs';
import GhostButton from '../../../action/button/ghost.gjs';
import InputBase from '../../input.gjs';
import ShiftFocusToParent from '../../../../../modifiers/shift-focus-to-parent';
import Super from '../../base.gjs';
import styles from './input.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const ClearButton = <template>
  <GhostButton @id={{@id}} class="spds-menu-search-input-clear" tabindex="-1" ...attributes>
    {{svgJar "x-mark" class="spds-menu-search-input-clear-icon"}}
  </GhostButton>
</template>;

// `@class` lets a consumer (e.g. the search trigger) re-apply a legacy class hook
// alongside the shared one without breaking external CSS selectors. svgJar is a helper,
// so it can't receive `...attributes` the way Input/ClearButton can.
const Icon = <template>
  {{svgJar (or @name "search") class=(concat "spds-menu-search-input-icon " (or @class ""))}}
</template>;

const InputComponent = <template>
  <InputBase
    @ariaLabel={{@ariaLabel}}
    @autofocus={{@autofocus}}
    @disabled={{@disabled}}
    @id={{@id}}
    @noBorder={{true}}
    @onInput={{@onInput}}
    @placeholder={{@placeholder}}
    @required={{@required}}
    @type="text"
    @value={{@value}}
    class="spds-menu-search-input-field"
    {{ShiftFocusToParent}}
    ...attributes
  />
</template>;

const METADATA = Metadata.define({
  description: `Presentational search field row shared by the search trigger and the in-menu search.

Yields contextual \`Icon\`, \`Input\`, and \`ClearButton\` building blocks so consumers can compose the
same row markup while owning their own container chrome, test hooks, and behavior (open/close, focus,
filtering). This keeps the visible search field consistent without duplicating the markup.`,
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'ariaLabel', defaultValue: 'Filter list' }]),
    pick(ALL_LAYOUT_ARGS, [{ name: 'gap', defaultValue: '8px' }]),
  ],
  name: 'Search Input',
});

export default class SpdsCoreMenuSearchInput extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-search-input';
  styles = styles;

  get ariaLabel() {
    return this.argOrDefault('ariaLabel');
  }

  get clearButtonId() {
    return this.composeSubcomponentId('clear-button');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get inputId() {
    return this.composeSubcomponentId('input');
  }

  <template>
    <FlexRow
      @alignItems="center"
      @gap={{this.gap}}
      @id={{this.id}}
      @justifyContent="center"
      class={{this.classes}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Icon=Icon
          Input=(component InputComponent ariaLabel=this.ariaLabel id=this.inputId)
          ClearButton=(component ClearButton id=this.clearButtonId)
        )
      }}
    </FlexRow>
  </template>
}

export const SEARCH_INPUT_METADATA = SpdsCoreMenuSearchInput.metadata;
